/** @format */

import {
  ProductPrice,
  ProductSku,
  ProductSkuPrices,
  ProductStylePrices,
} from '@shared/data-access/interfaces';
import { IntermediaryProductPrice } from '@pdp/data-access/interfaces';

const unsetProductPrice: ProductPrice = {
  minOfferPrice: '',
  maxOfferPrice: '',
  minListPrice: '',
  maxListPrice: '',
  minSavingPct: '',
  maxSavingPct: '',
  emptyPrice: false,
};

const isRange = (price: IntermediaryProductPrice) =>
  price.minofferprice !== price.maxofferprice || price.minlistprice !== price.maxlistprice;

const getStandardPrice = (price: IntermediaryProductPrice): ProductPrice => {
  // If Offer >= List and prices are valid (min <= max)
  if (
    (price.minofferprice === price.minlistprice && price.maxofferprice === price.maxlistprice) ||
    (price.minofferprice <= price.maxofferprice &&
      price.minlistprice <= price.maxlistprice &&
      (price.minofferprice >= price.minlistprice || price.maxofferprice >= price.maxlistprice))
  ) {
    return {
      ...unsetProductPrice,
      minOfferPrice: price.minofferprice,
      maxOfferPrice: isRange(price) ? price.maxofferprice : '',
    };
  }

  // MAP violation (Offer < List) or invalid prices
  return {
    ...unsetProductPrice,
    minListPrice: price.minlistprice,
    maxListPrice: price.maxlistprice,
    emptyPrice: true,
  };
};

const getDiscount = (listPrice: number, offerPrice: number) =>
  Math.floor(((listPrice - offerPrice) / listPrice) * 100);

const getRangeSalePrice = (price: IntermediaryProductPrice): ProductPrice => {
  let minSavingPct: number | string = '';
  let maxSavingPct: number | string = '';

  const minDiscount = getDiscount(price.minlistprice, price.minofferprice);
  const maxDiscount = getDiscount(price.maxlistprice, price.maxofferprice);

  if (minDiscount >= 5 || maxDiscount >= 5) {
    if (minDiscount < maxDiscount) {
      minSavingPct = minDiscount;
      maxSavingPct = maxDiscount;
    } else if (minDiscount > maxDiscount && maxDiscount >= 5) {
      minSavingPct = maxDiscount;
      maxSavingPct = minDiscount;
    } else {
      minSavingPct = minDiscount;
    }
  }

  return {
    ...unsetProductPrice,
    minOfferPrice: price.minofferprice,
    maxOfferPrice: price.maxofferprice,
    minListPrice: price.minlistprice,
    maxListPrice: price.maxlistprice,
    minSavingPct,
    maxSavingPct,
  };
};

// Sale price when prices are not a range
const getSimpleSalePrice = (price: IntermediaryProductPrice): ProductPrice => {
  const discount = getDiscount(price.minlistprice, price.minofferprice);

  return {
    ...unsetProductPrice,
    minOfferPrice: price.minofferprice,
    minListPrice: price.minlistprice,
    minSavingPct: discount >= 5 ? discount : '',
  };
};

const getSalePrice = (price: IntermediaryProductPrice): ProductPrice => {
  // Price with savings (if savings % >= 5)
  if (price.minofferprice < price.minlistprice || price.maxofferprice < price.maxlistprice) {
    return isRange(price) ? getRangeSalePrice(price) : getSimpleSalePrice(price);
  }

  // Missing prices
  if (!price.minofferprice && !price.maxofferprice) {
    return {
      ...unsetProductPrice,
      minListPrice: price.minlistprice,
      maxListPrice: price.maxlistprice,
      emptyPrice: true,
    };
  }

  // If not a range price and no savings, treat like normal non-sale price
  if (!isRange(price)) {
    return {
      ...unsetProductPrice,
      minOfferPrice: price.maxofferprice,
    };
  }

  // If is a range price and no savings, return blank price
  return unsetProductPrice;
};

/**
 * @description
 * Returns the "See Price in Cart" display price for MAP-restricted products
 */
export const getSeePriceInCartPrice = (price: IntermediaryProductPrice): ProductPrice => ({
  ...unsetProductPrice,
  minOfferPrice: price.minofferprice,
  maxOfferPrice: price.maxofferprice,
  minListPrice: price.minlistprice || '',
  maxListPrice: price.maxlistprice || '',
  emptyPrice: true,
});

export const isDisplayPriceMarkedDown = (price?: ProductPrice): boolean => {
  const { minListPrice, maxListPrice, minOfferPrice, maxOfferPrice } = price ?? {};

  if (!minListPrice) {
    return false;
  }

  const isMinPriceMarkedDown = Number(minListPrice) > Number(minOfferPrice);
  const isMaxPriceMarkedDown = Number(maxListPrice) > Number(maxOfferPrice);

  return isMinPriceMarkedDown || isMaxPriceMarkedDown;
};

/**
 * @description
 * Determines whether a sku is discounted using the exact same logic the UI price component uses.
 */
export const isSkuPriceDiscounted = (skuPrice: ProductSkuPrices): boolean =>
  isDisplayPriceMarkedDown(getProductPriceFromSku(skuPrice));

/**
 * @description
 * Determines whether a sku is on clearance using the sku's `clearance` flag.
 */
export const isSkuPriceClearance = (sku: ProductSku): boolean => sku.clearance;

/**
 * @description
 * Accepts a price object and returns productDisplay Price
 */
export const getPrice = (price: IntermediaryProductPrice): ProductPrice =>
  price.priceIndicator === 0 ? getSalePrice(price) : getStandardPrice(price);

/**
 * @description Converts a sku price into a product price
 * @param skuPrice - Sku price
 * @returns Sku price converted to a product price
 */
export const getProductPriceFromSku = (skuPrice: ProductSkuPrices): ProductPrice => {
  const price: IntermediaryProductPrice = {
    minofferprice: skuPrice.offerPrice,
    maxofferprice: skuPrice.offerPrice,
    minlistprice: skuPrice.listPrice,
    maxlistprice: skuPrice.listPrice,
    mapPriceIndicator: skuPrice.mapPriceIndicator,
    dealsPercentage: skuPrice.dealsPercentage,
    priceIndicator: skuPrice.priceIndicator,
  };

  return price.mapPriceIndicator === 1 ? getSeePriceInCartPrice(price) : getPrice(price);
};

/**
 * @description Converts a style (ecode) price into a product price
 * @param stylePrice - Style price
 * @returns Style (ecode) price converted to a product price
 */
export const getProductPriceFromStyle = (stylePrice: ProductStylePrices): ProductPrice => {
  // Math.abs is here to keep consistency with current behavior
  // Unclear if this was originally just done as an alternate way of converting strings to numbers or if handling negative prices is actually necessary
  const price: IntermediaryProductPrice = {
    minofferprice: Math.abs(Number(stylePrice.minofferprice)),
    maxofferprice: Math.abs(Number(stylePrice.maxofferprice)),
    minlistprice: Math.abs(Number(stylePrice.minlistprice)),
    maxlistprice: Math.abs(Number(stylePrice.maxlistprice)),
    mapPriceIndicator: stylePrice.mapPriceIndicator,
    priceIndicator: stylePrice.priceIndicator,
  };
  return price.mapPriceIndicator === 1 ? getSeePriceInCartPrice(price) : getPrice(price);
};
